import {
    META_MAP_DIR
} from '../useFileHandler';
import {
    mapFinder, prefixFileSeparator
} from './fileUtils';

const sortMapEntries = (map, mapDir, metaMap) => {
    const entries = Array.from(map.entries());
    return entries.sort((a, b) => {
        if (a[0] === META_MAP_DIR) return -1;
        if (b[0] === META_MAP_DIR) return 1;

        const aMetadata = metaMap.get(`${mapDir}/${a[0]}`);
        const bMetadata = metaMap.get(`${mapDir}/${b[0]}`);

        if (aMetadata?.type !== bMetadata?.type) {
            return aMetadata?.type === 'folder' ? -1 : 1;
        }

        return a[0].localeCompare(b[0]);
    });
};

export const fileMapHandler = (
    files,
    documentMap,
    metaMap,
    setDocumentMap,
    setMetaMap
) => {
    const mapAppender = (fileMap, metaMap, fileKey, externalMetadata = {}) => {
        const {
            loc_array
        } = prefixFileSeparator(fileKey);
        let currentMap = fileMap;
        // Keep only the first entry
        // if (currentMap.size > 1) {
        //     const firstEntry = Array.from(currentMap.entries())[0];
        //     currentMap.clear();
        //     currentMap.set(firstEntry[0], firstEntry[1]);
        // }
        let mapDir = '%__parentDir__%';

        loc_array.forEach((val, index) => {
            let level = index + 1;
            mapDir += `/${val}`;
            // if (val === '.keep') return; // Do not return early, mark as hidden instead
            if (!currentMap.has(val)) {
                const metadata = {
                    name: val,
                    type: index + 1 === loc_array.length ? 'file' : 'folder',
                    isSelected: false,
                    isHidden: val === '.keep',
                    mapDir,
                    level,
                    uploader: externalMetadata.userid,
                    origin: externalMetadata.origin,
                    otherData: externalMetadata.whole,
                };

                if (metadata.type === 'file') {
                    metadata.status = {
                        message: externalMetadata.reason,
                        status: externalMetadata.status,
                    };
                    metadata.l2_status = {
                        status: externalMetadata.l2_status,
                        message: externalMetadata.l2_summary,
                    };
                } else {
                    metadata.isOpened = false;
                }

                const newObj = new Map([[META_MAP_DIR, mapDir]]);
                metaMap.set(mapDir, metadata);
                currentMap.set(val, newObj);

                const sorted = sortMapEntries(currentMap, mapDir, metaMap);
                currentMap.clear();
                sorted.forEach(([key, value]) => currentMap.set(key, value));
            }
            currentMap = currentMap.get(val);
        });
    };

    setDocumentMap((prevMap) => {
        const newInstance = new Map(prevMap);
        const newMetaMapInstance = new Map(metaMap);
        const parentDir = newInstance.get('%__parentDir__%');

        files.forEach((file) => {
            mapAppender(parentDir, newMetaMapInstance, file.Key, {
                ...file.metadata,
                whole: file,
            });
        });

        setMetaMap(newMetaMapInstance);
        return newInstance;
    });
};

export const getParentDir = (fileKey) => {
    const splitString = fileKey.split('/');
    const parentDir = splitString.slice(0, -1).join('/');
    return parentDir;
};

export const listRefresher = (
    files,
    selectedFolder,
    setSelectedFolder,
    prevMetaMap,
    setDocumentMap,
    setMetaMap
) => {
    const mapAppender = (fileMap, metaMap, fileKey, externalMetadata = {}) => {
        const {
            loc_array
        } = prefixFileSeparator(fileKey);
        let currentMap = fileMap;
        let mapDir = '%__parentDir__%';

        loc_array.forEach((val, index) => {
            let level = index + 1;
            mapDir += `/${val}`;
            // if (val === '.keep') return;
            const isFile = index + 1 === loc_array.length;
            const existingMetadata = metaMap.get(mapDir);

            if (!currentMap.has(val)) {
                // Create new entry
                const metadata = {
                    name: val,
                    type: isFile ? 'file' : 'folder',
                    isSelected: false,
                    isHidden: val === '.keep',
                    mapDir,
                    level,
                    uploader: externalMetadata.userid,
                    origin: externalMetadata.origin,
                    otherData: externalMetadata.whole,
                };

                if (metadata.type === 'file') {
                    metadata.status = {
                        message: externalMetadata.reason,
                        status: externalMetadata.status,
                    };
                    metadata.l2_status = {
                        status: externalMetadata.l2_status,
                        message: externalMetadata.l2_summary,
                    };
                } else {
                    metadata.isOpened = false;
                }

                const newObj = new Map([[META_MAP_DIR, mapDir]]);
                metaMap.set(mapDir, metadata);
                currentMap.set(val, newObj);

                const sorted = sortMapEntries(currentMap, mapDir, metaMap);
                currentMap.clear();
                sorted.forEach(([key, value]) => currentMap.set(key, value));
            } else if (isFile && existingMetadata) {
                // Update existing file entry with new otherData (presigned URL)
                metaMap.set(mapDir, {
                    ...existingMetadata,
                    otherData: externalMetadata.whole,
                    status: {
                        message: externalMetadata.reason,
                        status: externalMetadata.status,
                    },
                    l2_status: {
                        status: externalMetadata.l2_status,
                        message: externalMetadata.l2_summary,
                    },
                });
            }
            currentMap = currentMap.get(val);
        });
    };

    // eslint-disable-next-line no-unused-vars
    setDocumentMap((prevMap) => {
        const newInstance = new Map([
            ['%__parentDir__%', new Map([[META_MAP_DIR, '%__parentDir__%']])],
        ]);
        const newMetaMapInstance = new Map(prevMetaMap);
        const parentDir = newInstance.get('%__parentDir__%');

        files.forEach((file) => {
            mapAppender(parentDir, newMetaMapInstance, file.Key, {
                ...file.metadata,
                whole: file,
            });
        });

        const selectedFolderLocation = selectedFolder.get(META_MAP_DIR);
        const selectedFolderMeta = prevMetaMap.get(
            selectedFolder.get(META_MAP_DIR)
        );

        newMetaMapInstance.set(selectedFolderLocation, selectedFolderMeta);

        const updatedFolder = mapFinder(newInstance, selectedFolderLocation);
        if (updatedFolder) {
            setSelectedFolder(updatedFolder);
        } else {
            let parent_folder = getParentDir(selectedFolderLocation);
            while (!mapFinder(newInstance, parent_folder)) {
                parent_folder = getParentDir(parent_folder);
            }
            let updatedFolderParent = mapFinder(newInstance, parent_folder);
            try {
                const updatedFolderLocation = updatedFolderParent.get(META_MAP_DIR);
                newMetaMapInstance.set(updatedFolderLocation, {
                    ...newMetaMapInstance.get(updatedFolderLocation),
                    isSelected: true,
                    isOpened: true,
                });
                setSelectedFolder(updatedFolderParent);
            } catch {
                console.warn('Missing Parent');
            }
        }
        setMetaMap(newMetaMapInstance);

        return newInstance;
    });
};
